import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, Grid } from '@mui/material';

import { AppDispatch, RootStateType } from '@Store';

import Title from '@Components/Title';
// import x from '@Components/Ta';

import Overview from './components/Overview';

import CancelAndTrackShipment from '@Components/CancelAndTrackShipment';
import Loader from '@Components/Loader';
import { SHIPMENT_STATUS } from '@Constants/Shipment';
import { resetShipmentError } from '@Reducers/AdminShipment';
import {
  getAdminShipmentById,
  getAdminShipmentError,
  getConnectOnWarehouse,
  getShipmentPaymentBySN,
} from '@Reducers/AdminShipment/actions';
import { getShipmentSettings } from '@Reducers/OrganizationSetup';
import PickupDropoff from './components/PickupDropoff';
import ShipmentCharges from './components/ShipmentCharges';
import ShipmentDetails from './components/ShipmentDetails';
import ShipmentError from './components/ShipmentError';
import { isActionAllowed } from '../../utils/allowedActions';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import ShipmentNotes from './components/ShipmentNotes';
import ShipmentPayment from './components/ShipmentPayments';
import ShipmentProcessingV2 from './components/ShipmentProcessingV2';
import ShipperConsignee from './components/ShipperConsignee';
import SuccessError from './components/SuccessError';
import TabsList from './components/Tabs';
import TrackingDetails from './components/TrackingDetails';
import RequiredDocuments from './components/RequiredDocuments';
import { getCreditLimit } from '@Reducers/Credit';
import BackButton from '@Components/BackButton';
import { useWebsocket } from '@Hook/useWebsocket';
import { fetchCarrierData } from '@Reducers/integration';
import API from '@Services/apiAxios';

type StatusDetail = {
  status: string;
  [key: string]: any;
};

type Shipment = {
  statusDetails: StatusDetail[];
  [key: string]: any;
};

function isInTransit(shipment?: Shipment): boolean {
  if (!shipment || !shipment.statusDetails) return false;
  return shipment.statusDetails.some(detail => detail.status === 'in_transit');
}

const formatPickupDate = (date: any): string => {
  if (!date) return '';
  if (typeof date?.toDate === 'function') {
    return date.toDate().toUTCString();
  }
  const parsed = new Date(date);
  if (!isNaN(parsed.getTime())) {
    return parsed.toUTCString();
  }
  return String(date);
};

export function AdminSingleShipment() {
  const dispatch = useDispatch<AppDispatch>();
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const { mutlipleShipmentsData } = useSelector((state: RootStateType) => state.trackShipment) || [];

  const [clearanceDocs, setClearanceDocs] = useState<string[]>([]);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);

  const isShipperConsigneeDetailsAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipper_or_consignee_details,
    PERFORM_ACTION.read,
  );

  const isPickupDropoffAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.pick_up_or_drop_off,
    PERFORM_ACTION.read,
  );

  const isVoidAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.void_shipment,
    PERFORM_ACTION.write,
  );

  const isCancelAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.cancel_shipment,
    PERFORM_ACTION.write,
  );

  const isShipmentDetailsAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_details,
    PERFORM_ACTION.read,
  );

  const isShipmentProcessingAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_processing,
    PERFORM_ACTION.read,
  );

  const isTrackingDetailsAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.tracking_details,
    PERFORM_ACTION.read,
  );

  const isShipmentChargesAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_charges,
    PERFORM_ACTION.read,
  );

  const isShipmentNotesAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_notes,
    PERFORM_ACTION.read,
  );

  const isPaymentAllowed = isActionAllowed(
    accessGroupById?.actions,
    ACTION_NAMES.Single_Shipment_View_OR_Processing.payment,
    PERFORM_ACTION.read,
  );

  const { sn: shipmentNumber } = useParams();
  const { shipmentById } = useSelector((state: RootStateType) => state.adminShipment);
  const loading = useSelector(
    (state: RootStateType) =>
      state.adminShipment.loading ||
      state.admin.loading ||
      state.Warehouse.loading ||
      state.trackShipment.loading ||
      state.shipmentHistory.loading ||
      state.getRates.loading ||
      state.shipment.loading,
  );
  const isUnprocessedShipment = shipmentById?.status === SHIPMENT_STATUS.unprocessed.value;

  useEffect(() => {
    if (shipmentNumber) {
      dispatch(fetchCarrierData());
      dispatch(getShipmentSettings());
      dispatch(getAdminShipmentById(shipmentNumber));
      dispatch(getShipmentPaymentBySN(shipmentNumber));
      dispatch(getConnectOnWarehouse(shipmentNumber));
    }

    return () => {
      dispatch(resetShipmentError());
    };
  }, [shipmentNumber, dispatch]);

  useEffect(() => {
    if (isUnprocessedShipment && shipmentNumber) {
      dispatch(getAdminShipmentError(shipmentNumber));
    }
  }, [shipmentById?.status]);

  useEffect(() => {
    if (shipmentById?._accountId?.isAccountHolder) {
      const idToUse = shipmentById._accountId?._id;
      dispatch(getCreditLimit(idToUse));
    }
  }, [shipmentById?._accountId]);

  useEffect(() => {
    if (!shipmentById?._consigneeId?.country) {
      setClearanceDocs([]);
      return;
    }

    let active = true;

    const fetchClearanceDocs = async () => {
      setLoadingDocs(true);
      try {
        const payload = {
          consignee: {
            country: shipmentById?._consigneeId?.country || '',
          },
          package: {
            unitOfMeasurement: shipmentById?._packageId?.unitOfMeasurement || 'kg-cm',
            packaging: !!shipmentById?._packageId?.packaging,
            insurance: !!shipmentById?._packageId?.insurance,
            isDocument: !!shipmentById?._packageId?.isDocument,
            isCod: !!shipmentById?._packageId?.isCod,
            isDropOff: !!shipmentById?._packageId?.isDropOff,
            pickupDate: formatPickupDate(shipmentById?._packageId?.pickupDate),
            timeSlot: shipmentById?._packageId?.timeSlot || '',
            codAmount: Number(shipmentById?._packageId?.codAmount) || 0,
            currency: shipmentById?._packageId?.currency || 'INR',
            box: (shipmentById?._packageId?.box || []).map((box: any, idx: number) => ({
              length: Number(box.length) || 0,
              width: Number(box.width) || 0,
              height: Number(box.height) || 0,
              weight: Number(box.weight) || 0,
              id: box.id || idx.toString(),
              products: (box.products || []).map((prod: any) => ({
                description: prod.description || '',
                quantity: Number(prod.quantity) || 0,
                unitValue: Number(prod.unitValue) || 0,
                HSNName: prod.HSNName || '',
                HSNCode: prod.HSNCode || '',
                _product: prod._product || null,
                _id: prod._id || prod.id || '',
                createdAt: prod.createdAt || new Date().toISOString(),
                updatedAt: prod.updatedAt || new Date().toISOString(),
              })),
            })),
            totalBoxWeight: 'Need calc',
            invoiceNumber: shipmentById?._packageId?.invoiceNumber || '',
            dutyCharge: {
              type:
                typeof shipmentById?.dutyCharge === 'object' && shipmentById?.dutyCharge?.type
                  ? shipmentById.dutyCharge.type
                  : typeof shipmentById?.dutyCharge === 'string'
                  ? shipmentById.dutyCharge
                  : 'DDP',
              charge:
                typeof shipmentById?.dutyCharge === 'object' && shipmentById?.dutyCharge?.charge !== undefined
                  ? Number(shipmentById.dutyCharge.charge)
                  : 0,
            },
          },
          purposeOfShipment: (shipmentById?.extraDetails as any)?.other?.purposeOfShipment || 'FOR_SALE',
        };

        const response = await API.post('/speedboxbot/clearance-documents', payload);

        if (active) {
          const docs = response?.data?.documents || [];
          const docNames = docs.map((doc: any) => doc.documentName).filter(Boolean);
          setClearanceDocs(docNames);
        }
      } catch (err) {
        console.error('Error fetching clearance documents for admin shipment:', err);
        if (active) {
          setClearanceDocs([]);
        }
      } finally {
        if (active) {
          setLoadingDocs(false);
        }
      }
    };

    fetchClearanceDocs();

    return () => {
      active = false;
    };
  }, [shipmentById?._id, shipmentById?.shipmentNumber]);

  useWebsocket({
    query: {
      channel: 'shipment-update-' + shipmentNumber,
    },
  });

  const checkForIsInTransit = isInTransit(mutlipleShipmentsData?.[0]);

  return (
    <Grid container>
      <Loader loading={loading} overly>
        <>
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <BackButton link="/shipment/history" />
            <Title title="Shipment Details " removeBorder />
          </Box>
          <SuccessError />
          <Overview />
          <Grid container justifyContent={'space-between'} sx={{ alignItems: 'center' }}>
            <ShipmentError sellingChargeMsgShow={false} />
            {shipmentById?.status && (
              <CancelAndTrackShipment
                isInTransit={checkForIsInTransit}
                shipmentStatus={shipmentById.status}
                accountId={shipmentById?._userId._id}
                isVoidAllowed={isVoidAllowed}
                isCancelAllowed={isCancelAllowed}
              />
            )}
          </Grid>
          <TabsList hasRequiredDocuments={clearanceDocs.length > 0} />
          {isShipperConsigneeDetailsAllowed && <ShipperConsignee />}
          {isPickupDropoffAllowed && <PickupDropoff />}
          {isShipmentDetailsAllowed && <ShipmentDetails />}
          {!isUnprocessedShipment && (
            <>
              {/*   {isShipmentProcessingAllowed && <ShipmentProcessing />} */}
              {isShipmentProcessingAllowed && <ShipmentProcessingV2 />}
              {isShipmentNotesAllowed && <ShipmentNotes />}
              {isTrackingDetailsAllowed && <TrackingDetails />}
              {isShipmentChargesAllowed && <ShipmentCharges />}
              {isPaymentAllowed && <ShipmentPayment />}
            </>
          )}
          {clearanceDocs.length > 0 && <RequiredDocuments clearanceDocs={clearanceDocs} loading={loadingDocs} />}
        </>
      </Loader>
    </Grid>
  );
}

export default AdminSingleShipment;

// Below files are not used anytwhere, thus remove :
// src/pages/ViewSingleShipmentAdmin/components/BoxDimensionsInfo
// src/pages/ViewSingleShipmentAdmin/components/BoxesDetails
